import React, { useMemo, useContext } from "react";
import { ITicket } from "../../types/dashboard";
import "./CalendarGridWeek.css";
import { getTicketColorByType } from "./CalendarUtils";
import { formatTimeForInputValue, getLocalizedDays, getTimezoneAwareDay, getTimezoneAwareDate, setTimezoneAwareHours } from "../../utils/dateFormats";
import { AppContext } from "../../appContext";

const CalendarGridWeekTicket = ({ticket, onTicketClick}: {ticket: ITicket, onTicketClick?: ()=>void})=>{
    return <div className={`button calendarGridWeek_ticket ${getTicketColorByType(ticket.type)}`} onClick={onTicketClick}>
        <span className="calendarGrid_ticket_date">
            {formatTimeForInputValue(ticket.start)}
        </span>
        <span className="calendarGrid_ticket_text">
            {ticket.title}
        </span>
    </div>
}

interface ICalendarGridWeekProps{
    baseDate?: Date,
    tickets: ITicket[],
    columns: number,
    onTimeClick?: (time: Date)=>void,
    onTicketClick?: (ticket: ITicket)=>void
}

const getWeekDates = (baseDate: Date) => {
    const dates: Date[] = [];
    const currentDate = new Date(baseDate);

    // Find Monday of the current week using local day
    const dayOfWeek = currentDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Sunday is 0, Monday is 1

    currentDate.setDate(currentDate.getDate() + mondayOffset);
    currentDate.setHours(0, 0, 0, 0); // Normalize to start of day

    // Add 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
        const date = new Date(currentDate);
        date.setDate(currentDate.getDate() + i);
        dates.push(date);
    }

    return dates;
};

export const CalendarGridWeek = ({baseDate: _baseDate, tickets, onTimeClick, onTicketClick}: ICalendarGridWeekProps)=>{
    const baseDate = _baseDate || new Date();
    const { translate } = useContext(AppContext);
    const localizedDays = getLocalizedDays(translate);

    const weekDates = getWeekDates(baseDate);

    const handleDayClick = (date: Date) => {
        onTimeClick?.(date);
    };

    const formatMonthYear = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = { 
            year: 'numeric', 
            month: 'long'
        };
        return date.toLocaleDateString(undefined, options);
    };

    return (
        <div className="calendarGridWeek">
            <div className="calendarGridWeek_header">
                <h2 className="calendarGridWeek_date">{formatMonthYear(baseDate)}</h2>
            </div>
            <div className="calendarGridWeek_dates">
                {localizedDays.map((dayName, i)=>{
                    return (
                        <div className="calendarGridWeek_day calendarGridWeek_day--head" style={{
                            borderLeft: (i % 7 != 0) ? "none" : ""
                        }} key={i}>
                            {dayName.toUpperCase()}
                        </div>
                    )
                })}
            </div>
            <div className="calendarGridWeek_dates calendarGridWeek_dates--main">
                {weekDates.map((date, i)=>{
                    const foundTickets = tickets.filter(ticket=>{
                        const start = new Date(date);
                        start.setHours(0, 0, 0, 0); // Start of day
                        const end = new Date(date);
                        end.setHours(23, 59, 59, 999); // End of day
                        return start <= ticket.start && end >= ticket.start;
                    });

                    return (
                        <div 
                            className="calendarGridWeek_day" 
                            style={{
                                borderLeft: (i % 7 != 0) ? "none" : "",
                                borderTop: 'none'
                            }}
                            key={i}
                            onClick={() => handleDayClick(date)}
                        >
                            <div className="calendarGridWeek_day_number">
                                {getTimezoneAwareDate(date)}
                            </div>
                            {foundTickets.map(ticket=>{
                                return <CalendarGridWeekTicket 
                                    key={ticket.start.getTime()} 
                                    ticket={ticket} 
                                    onTicketClick={()=>onTicketClick?.(ticket)}
                                />
                            })}
                        </div>
                    )
                })}
            </div>
        </div>
    );
}